/**
 * Phase 33 — Micro-routing lightweight per multi-order driver.
 *
 * Strategia:
 * - Greedy nearest-neighbor (no TSP): partendo dalla posizione attuale del driver,
 *   ogni step scegliamo l'ordine il cui *pickup* è più vicino (se non già ritirato)
 *   oppure il delivery più vicino (se l'ordine è già stato ritirato).
 * - Per ogni ordine, conserviamo il flag "pickup-done" inferito dallo stato:
 *     stati pre-pickup: accepted, picking_up
 *     stati post-pickup: picked_up, delivering
 *
 * Output: lista degli ordini riordinati con `next_stop = "pickup"|"delivery"` + km stimati.
 *
 * NB: questo è un'euristica UI. Lo stato e i payout restano invariati per ordine.
 */

function haversineKm(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

const POST_PICKUP = new Set(["picked_up", "delivering"]);

function nextStopCoords(o) {
  if (POST_PICKUP.has(o.status)) {
    return Number.isFinite(o.delivery_lat) && Number.isFinite(o.delivery_lng)
      ? { lat: o.delivery_lat, lng: o.delivery_lng, kind: "delivery" } : null;
  }
  return Number.isFinite(o.pickup_lat) && Number.isFinite(o.pickup_lng)
    ? { lat: o.pickup_lat, lng: o.pickup_lng, kind: "pickup" } : null;
}

/**
 * Returns a new array of orders re-ordered greedily from `start` (driver position).
 * Orders without usable coords are appended at the end (preserving relative order).
 */
export function optimizeRoute(orders, start) {
  if (!Array.isArray(orders) || orders.length <= 1) return orders || [];
  if (!start || !Number.isFinite(start.lat) || !Number.isFinite(start.lng)) return orders;

  const annotated = orders.map((o) => ({ o, next: nextStopCoords(o) }));
  const usable = annotated.filter((x) => x.next);
  const unusable = annotated.filter((x) => !x.next).map((x) => x.o);

  const sorted = [];
  let cursor = { lat: start.lat, lng: start.lng };
  while (usable.length) {
    let bestI = 0;
    let bestD = Infinity;
    for (let i = 0; i < usable.length; i++) {
      const u = usable[i];
      const d = haversineKm(cursor.lat, cursor.lng, u.next.lat, u.next.lng);
      if (d < bestD) { bestD = d; bestI = i; }
    }
    const picked = usable.splice(bestI, 1)[0];
    sorted.push({ ...picked.o, _suggested_next: picked.next.kind, _suggested_km: +bestD.toFixed(2) });
    cursor = { lat: picked.next.lat, lng: picked.next.lng };
  }
  return [...sorted, ...unusable];
}

/** Indicates if reorder differs from the input order (so we can highlight in UI). */
export function isReordered(original, suggested) {
  if (!Array.isArray(original) || !Array.isArray(suggested)) return false;
  if (original.length !== suggested.length) return false;
  for (let i = 0; i < original.length; i++) {
    if (original[i].id !== suggested[i].id) return true;
  }
  return false;
}
